import { useState, useEffect } from 'react';

/**
 * Custom hook for calculating menu position relative to a trigger element
 * @param {Object} options - Configuration options
 * @param {React.RefObject} options.triggerRef - Ref to the trigger element
 * @param {React.RefObject} options.menuRef - Ref to the menu element
 * @param {boolean} options.isOpen - Whether the menu is open
 * @param {number} options.estimatedHeight - Estimated height for initial positioning
 * @param {number} [options.rightOffset=0] - Offset from right edge
 * @param {boolean} [options.alignTopWithTriggerTop=true] - If true, align menu top with trigger top when below; if false, align with trigger bottom
 * @param {HTMLElement} [options.triggerElement] - Optional trigger element to force recalculation when it changes
 * @returns {Function} Function that returns position styles
 */
function useMenuPosition({
  triggerRef,
  menuRef,
  isOpen,
  estimatedHeight,
  rightOffset = 0,
  alignTopWithTriggerTop = true,
  triggerElement = null,
}) {
  const [position, setPosition] = useState(null);
  const [isMeasuring, setIsMeasuring] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      setIsMeasuring(false);
      return;
    }

    if (!triggerRef.current) {
      return;
    }

    const rect = triggerRef.current.getBoundingClientRect();
    const right = window.innerWidth - rect.left + rightOffset;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldPositionAbove =
      spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

    // Set initial position
    if (shouldPositionAbove) {
      const bottom = window.innerHeight - rect.bottom;
      setPosition({
        bottom: Math.max(bottom, 0),
        right,
      });
    } else {
      // Position below: align based on alignTopWithTriggerTop option
      const top = alignTopWithTriggerTop ? rect.top : rect.bottom;
      setPosition({
        top,
        right,
      });
    }

    // Start measuring phase to refine position
    setIsMeasuring(true);

    // Calculate refined position after menu is rendered and measured
    const updatePosition = () => {
      if (!menuRef.current || !triggerRef.current) {
        setIsMeasuring(false);
        return;
      }

      const menuHeight = menuRef.current.offsetHeight;
      const refinedRect = triggerRef.current.getBoundingClientRect();
      const refinedSpaceBelow = window.innerHeight - refinedRect.bottom;
      const refinedSpaceAbove = refinedRect.top;
      const refinedShouldPositionAbove =
        refinedSpaceBelow < menuHeight && refinedSpaceAbove > refinedSpaceBelow;

      const refinedRight = window.innerWidth - refinedRect.left + rightOffset;

      // Refine position ensuring it stays within viewport bounds
      if (refinedShouldPositionAbove) {
        const bottom = window.innerHeight - refinedRect.bottom;
        const maxBottom = window.innerHeight;
        setPosition({
          bottom: Math.min(Math.max(bottom, 0), maxBottom),
          right: refinedRight,
        });
      } else {
        const top = alignTopWithTriggerTop
          ? refinedRect.top
          : refinedRect.bottom;
        const maxTop = window.innerHeight - menuHeight;
        setPosition({
          top: Math.min(Math.max(top, 0), maxTop),
          right: refinedRight,
        });
      }
      setIsMeasuring(false);
    };

    // Use double requestAnimationFrame to ensure DOM is fully updated and measured
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        updatePosition();
      });
    });
  }, [
    isOpen,
    estimatedHeight,
    rightOffset,
    alignTopWithTriggerTop,
    triggerRef,
    menuRef,
    triggerElement,
  ]);

  const getPosition = () => {
    if (!position) {
      return {
        top: 0,
        right: 0,
        opacity: 0,
        pointerEvents: 'none',
        position: 'fixed',
      };
    }

    if (isMeasuring) {
      return {
        ...position,
        opacity: 0,
        pointerEvents: 'none',
        position: 'fixed',
      };
    }

    return {
      ...position,
      opacity: 1,
      pointerEvents: 'auto',
      position: 'fixed',
    };
  };

  return getPosition;
}

export default useMenuPosition;

